import { container, inject, singleton } from "tsyringe";
import { integer } from "vscode-languageserver-protocol";
import { User } from "../models/user";
import { UserLabels } from "../models/user-labels";
import { UserSpace } from "../models/user-space";
import { OrganizationUser } from "../models/organization-user-view-model";
import { UserSpaces } from "../models/user-spaces";
import { AuthService } from "./auth.service";
import { BaseApi } from "./base.api";
import { ApiKey } from "../models/api-key";
import { CompleteVerificationRequest, ResendVerificationEmailRequest, InviteUserRequest } from '../models/user-verification.models';
import { LoginToken } from "../models/login";
import { UserLoginType } from "../models/user-login-type-model";

@singleton()
export class UserApi extends BaseApi {
    private _authService: AuthService;

    constructor() {
        super();
        this._authService = container.resolve(AuthService);
    }

    async getAsync(id: number) {
        return this.getObjectAsync<User>(`/api/user/${id}`, this._authService.token);
    }

    async getSpacesAsync(pageIndex = 1, recordsPerPage = 100, sortColumn = "", sortOrder = -1) {
        return this.getObjectAsync<UserSpaces>(
            `/api/user/spaces?pageIndex=${pageIndex}&recordsPerPage=${recordsPerPage}&sortColumn=${sortColumn}&sortOrder=${sortOrder}`,
            this._authService.token
        );
    }
    async getLabelsAsync(spaceId: number, pageIndex = 1, recordsPerPage = 100, sortColumn = "", sortOrder = -1) {
        return this.getObjectAsync<UserLabels>(
            `/api/user/labels/space/${spaceId}?pageIndex=${pageIndex}&recordsPerPage=${recordsPerPage}&sortColumn=${sortColumn}&sortOrder=${sortOrder}`,
            this._authService.token
        );
    }
    async getHomeLabelsAsync(pageIndex = 1, recordsPerPage = 100, sortColumn = "", sortOrder = -1) {
        return this.getObjectAsync<UserLabels>(
            `/api/user/labels/space/home?pageIndex=${pageIndex}&recordsPerPage=${recordsPerPage}&sortColumn=${sortColumn}&sortOrder=${sortOrder}`,
            this._authService.token
        );
    }

    //async ResetPassword(userId: integer, email: string, password: string) {
    //    return this.postObjectAsync(`/api/user/${userId}/reset-password`, { email: email, password: password }, this._authService.token);

    //}

    async createAsync(user: User) {
        return this.postObjectAsync(`/api/user?source=controlcenter`, user, this._authService.token);
    }

    async deleteAllAsync(userIds: number[]) {
        return this.deleteAllObjectsAsync(`/api/user/all`, { ids: userIds }, this._authService.token);
    }

    async deleteAsync(id: number) {
        return this.deleteObjectAsync(`/api/user/${id}`, this._authService.token);
    }

    async restoreAccessAsync(userId: number) {
        return this.putAsync(`/api/user/${userId}/restore`, this._authService.token);
    }

    async approveUserAsync(userId: number) {
        return this.putAsync(`/api/user/${userId}/approve`, this._authService.token);
    }

    async ChangePassword(currentPassword: string, newPassword: string) {
        return this.postObjectAsync(
            `/api/user/change-password`,
            {currentPassword: currentPassword, newPassword: newPassword },
            this._authService.token
        );
    }

    async ResetPassword(token: string, password: string) {
        return this.postObjectAsync(`/api/user/reset-password`, { token: token, password: password }, this._authService.token);
    }
    async updateAsync(userId: number, user: User) {
        return this.putObjectAsync(`/api/user/${userId}`, user, this._authService.token);
    }

    async changeOrganizationAsync(newOrgId: number) {
        return this.postAsync(`/api/user/change-org/${newOrgId}`, this._authService.token);
    }

    /**
     * Get api keys for the currently logged in user
     * @returns
     */
    async getUserApiKeysAsync() {
        return this.getObjectAsync<{ userApiKeys: ApiKey[]; userName: string }>(`/api/user/api-keys`, this._authService.token);
    }

    /**
     * Get api keys for the userId
     * @param userId
     * @returns
     */
    async getApiKeysAsync(userId: number) {
        return this.getObjectAsync<{ userApiKeys: ApiKey[]; userName: string }>(`/api/user/${userId}/api-keys`, this._authService.token);
    }

    /**
     * Get the api key for the userid
     * @param userId
     * @param apiKeyId
     * @returns
     */
    async getApiKeyAsync(userId: number, apiKeyId: number) {
        return this.getObjectAsync<ApiKey>(`/api/user/${userId}/api-key/${apiKeyId}`, this._authService.token);
    }

    /**
     * Get the api key for the currently logged in user
     * @param apiKeyId
     * @returns
     */
    async getUserApiKeyAsync(apiKeyId: number) {
        return this.getObjectAsync<ApiKey>(`/api/user/api-key/${apiKeyId}`, this._authService.token);
    }

    /**
     * Update the api key for the userId
     * @param userId
     * @param apiKeyId
     * @param apiKey
     * @returns
     */
    async updateApiKeyAsync(userId: number, apiKeyId: number, apiKey: ApiKey) {
        return this.putObjectAsync(`/api/user/${userId}/api-key/${apiKeyId}`, apiKey, this._authService.token);
    }

    /**
     * Update the api key for the currently logged in user
     * @param userId
     * @param apiKeyId
     * @param apiKey
     * @returns
     */
    async updateUserApiKeyAsync(apiKeyId: number, apiKey: ApiKey) {
        return this.putObjectAsync(`/api/user/api-key/${apiKeyId}`, apiKey, this._authService.token);
    }

    /**
     * create an api key for the userId
     * @param userId
     * @param apiKey
     * @returns
     */
    async createApiKeyAsync(userId: number, apiKey: ApiKey) {
        return this.postObjectReturnObjectAsync<ApiKey>(`/api/user/${userId}/api-key`, apiKey, this._authService.token);
    }

    /**
     * Create an api key for the currently logged in user
     * @param apiKey
     * @returns
     */
    async createUserApiKeyAsync(apiKey: ApiKey) {
        return this.postObjectReturnObjectAsync<ApiKey>(`/api/user/api-key`, apiKey, this._authService.token);
    }

    /**
     * Delete the api key for the user id
     * @param userId
     * @param id
     * @returns
     */
    async deleteApiKeyAsync(userId: number, id: number) {
        return this.deleteObjectAsync(`/api/user/${userId}/api-key/${id}`, this._authService.token);
    }

    /**
     * Delete the api key for the currently logged in user
     * @param userId
     * @param id
     * @returns
     */
    async deleteUserApiKeyAsync(id: number) {
        return this.deleteObjectAsync(`/api/user/api-key/${id}`, this._authService.token);
    }

    /**
     * Delete all selected api keys for the userid
     * @param userId
     * @param apiKeyIds
     * @returns
     */
    async deleteAllApiKeysAsync(userId: number, apiKeyIds: number[]) {
        return this.deleteAllObjectsAsync(`/api/user/${userId}/api-keys/all`, { ids: apiKeyIds }, this._authService.token);
    }

    /**
     * Delete all selected api keys for the currently logged in user
     * @param userId
     * @param apiKeyIds
     * @returns
     */
    async deleteAllUserApiKeysAsync( apiKeyIds: number[]) {
        return this.deleteAllObjectsAsync(`/api/user/api-keys/all`, { ids: apiKeyIds }, this._authService.token);
    }

    async inviteUserAsync(inviteData: InviteUserRequest) {
        return this.postObjectAsync(`/api/user/invite`, inviteData, this._authService.token);
    }

    async resendInviteAsync(email: string) {
        const request: ResendVerificationEmailRequest = { email };
        return this.postObjectAsync(`/api/user/resend-verification`, request, this._authService.token);
    }

    async completeVerificationAsync(verificationData: CompleteVerificationRequest) {
        const res = await this.postObjectReturnObjectAsync<string>(`/api/user/complete-verification`, verificationData, null);
        if (res.isOk) {
            return res;
        }
        else {
            throw res.err;
        }
    }

    async verifyEmailAsync(token: string, name: string, organizationName: string, password: string) {
        const res = await this.postObjectReturnObjectAsync<boolean>(`/api/user/verify-email/${token}`, { name: name, organizationName: organizationName, password: password });
        if (res.isOk) {
            return res;
        }
        else {
            throw res.err;
        }
    }

    async resendRegistrationVerificationAsync(request: ResendVerificationEmailRequest) {
        return this.postObjectAsync('api/user/resend-registration-verification', request, null);
    }

    async hideWelcomeScreenAsync() {
        return this.putAsync(`/api/user/hide-welcome`, this._authService.token);
    }

    async getUserLoginType(email: string) {
        return this.postObjectReturnObjectAsync<UserLoginType>(`/api/user/userlogintype`, email);
    }

    async getAuthServiceType(token: string) {
        return this.postObjectReturnObjectAsync<number>(`/api/user/authservicetype`, token);
    }
}
